import React, { useState, useEffect, useRef } from "react";
import type {
    CellTemplate,
    Cell,
    Compatible,
    Uncertain,
    UncertainCompatible,
} from "@silevis/reactgrid";
import { keyCodes } from "@silevis/reactgrid";

/**
 * PercentageCell — stores a decimal fraction (0.8), displays as "80%".
 *
 * UX contract:
 *   - Read mode:   displays `Math.round(value * 100)` + "%" (e.g. 0.8 → "80%")
 *   - Edit mode:   user types a whole number (e.g. 80); on commit the template
 *                  divides by 100 and emits 0.8 as the new value.
 *   - Key press:   entering a digit immediately starts edit mode with that character.
 */
export interface PercentageCell extends Cell {
    type: "percentage";
    value: number; // stored as decimal fraction, e.g. 0.8
    nonEditable?: boolean;
    /** Transient: initial key character when entering edit mode via keypress. */
    initialKey?: string;
}

export class PercentageCellTemplate implements CellTemplate<PercentageCell> {
    getCompatibleCell(
        uncertainCell: Uncertain<PercentageCell>,
    ): Compatible<PercentageCell> {
        const value =
            typeof uncertainCell.value === "number" &&
            !isNaN(uncertainCell.value)
                ? uncertainCell.value
                : 0;
        const text = String(Math.round(value * 100));
        return { ...uncertainCell, value, text } as Compatible<PercentageCell>;
    }

    isFocusable = () => true;

    update(
        cell: Compatible<PercentageCell>,
        cellToMerge: UncertainCompatible<PercentageCell>,
    ): Compatible<PercentageCell> {
        // Detect internal copy-paste: ReactGrid stores the full cell as JSON in
        // a data-reactgrid HTML attribute. When pasting back, cellToMerge already
        // has `type: "percentage"` and `value` as the decimal fraction (0.8).
        // In that case, use the value directly (don't divide by 100 again).
        //
        // For user-typed values from the editor, cellToMerge has
        // `type: "percentage"` too, but value is the WHOLE number (80).
        // The editor passes a `_isEditorCommit` flag to distinguish.
        const isEditorCommit = (cellToMerge as any)._isEditorCommit === true;

        if (isEditorCommit) {
            // From editor: value is whole number (80) → divide by 100
            const typed =
                typeof cellToMerge.value === "number" &&
                !isNaN(cellToMerge.value)
                    ? cellToMerge.value
                    : 0;
            return this.getCompatibleCell({
                ...cell,
                value: typed / 100,
                initialKey: undefined,
            });
        }

        // From paste or fill: value is already the decimal fraction (0.8) → use as-is
        const val =
            typeof cellToMerge.value === "number" && !isNaN(cellToMerge.value)
                ? cellToMerge.value
                : 0;
        return this.getCompatibleCell({
            ...cell,
            value: val,
            initialKey: undefined,
        });
    }

    handleKeyDown(
        cell: Compatible<PercentageCell>,
        keyCode: number,
        ctrl: boolean,
        shift: boolean,
        alt: boolean,
        key?: string,
    ): { cell: Compatible<PercentageCell>; enableEditMode: boolean } {
        if (ctrl || alt) return { cell, enableEditMode: false };

        // Double-click (POINTER) or Enter → open edit mode with current value
        if (keyCode === keyCodes.POINTER || keyCode === keyCodes.ENTER) {
            return { cell, enableEditMode: true };
        }

        const isDigit = keyCode >= 48 && keyCode <= 57;
        const isNumpad = keyCode >= 96 && keyCode <= 105;
        const isDot = keyCode === 190 || keyCode === 110;
        const isDash = keyCode === 189 || keyCode === 109;

        // Typing a character → enter edit mode with that character as seed
        if (isDigit || isNumpad || isDot || isDash) {
            const newCell = this.getCompatibleCell({
                ...cell,
                initialKey: key || "",
            });
            return { cell: newCell, enableEditMode: true };
        }

        // Delete/Backspace → clear to 0
        if (keyCode === 46 || keyCode === 8) {
            return {
                cell: this.getCompatibleCell({
                    ...cell,
                    value: 0,
                    initialKey: undefined,
                }),
                enableEditMode: false,
            };
        }

        return { cell, enableEditMode: false };
    }

    render(
        cell: Compatible<PercentageCell>,
        isInEditMode: boolean,
        onCellChanged: (
            cell: Compatible<PercentageCell>,
            commit: boolean,
        ) => void,
    ): React.ReactNode {
        if (!isInEditMode) {
            return (
                <div
                    style={{
                        width: "100%",
                        textAlign: "right",
                        padding: "0 4px",
                        userSelect: "none",
                    }}
                >
                    {Math.round(cell.value * 100)}%
                </div>
            );
        }

        return (
            <PercentageCellEditor cell={cell} onCellChanged={onCellChanged} />
        );
    }
}

// ── Editor component ───────────────────────────────────────────────────────

interface EditorProps {
    cell: Compatible<PercentageCell>;
    onCellChanged: (cell: Compatible<PercentageCell>, commit: boolean) => void;
}

const PercentageCellEditor: React.FC<EditorProps> = ({
    cell,
    onCellChanged,
}) => {
    // If we entered edit mode via a key press, seed input with that character.
    // Otherwise start with the current display value.
    const initialValue =
        (cell as any).initialKey != null
            ? (cell as any).initialKey
            : String(Math.round(cell.value * 100));

    const [inputValue, setInputValue] = useState(initialValue);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
            if ((cell as any).initialKey != null) {
                // Cursor at end when seeded from keypress
                const len = inputRef.current.value.length;
                inputRef.current.setSelectionRange(len, len);
            } else {
                inputRef.current.select();
            }
        }
        // Notify ReactGrid of the initial value so currentlyEditedCell
        // is up-to-date. Without this, a single-digit type + blur would
        // commit the stale original value.
        notifyReactGrid(initialValue, false);
    }, []); // Run once on mount

    const notifyReactGrid = (raw: string, commit: boolean) => {
        const num = parseFloat(raw);
        const wholeNumber = isNaN(num) ? 0 : num;
        // _isEditorCommit flag tells update() this value is a whole number (80),
        // NOT a decimal fraction (0.8) from paste.
        onCellChanged(
            { ...cell, value: wholeNumber, _isEditorCommit: true } as any,
            commit,
        );
    };

    return (
        <input
            ref={inputRef}
            inputMode="decimal"
            value={inputValue}
            style={{
                width: "100%",
                border: "none",
                outline: "none",
                background: "transparent",
                textAlign: "right",
                fontSize: "inherit",
                fontFamily: "inherit",
                color: "inherit",
                padding: "0 4px",
            }}
            onChange={(e) => {
                setInputValue(e.target.value);
                // CRITICAL: Notify ReactGrid of the new value with commit=false.
                // ReactGrid stores this as `currentlyEditedCell`. When the user
                // clicks away, ReactGrid commits currentlyEditedCell automatically.
                // Without this, ReactGrid would commit the stale original value.
                notifyReactGrid(e.target.value, false);
            }}
            onBlur={() => notifyReactGrid(inputValue, true)}
            onKeyDown={(e) => {
                // Let Enter propagate to ReactGrid — it will commit currentlyEditedCell
                // (which we keep updated via onChange → onCellChanged(cell, false))
                // and exit edit mode automatically.
                if (e.key === "Enter") return;

                // Let Escape propagate to ReactGrid — it discards currentlyEditedCell
                // and exits edit mode without committing.
                if (e.key === "Escape") return;

                // Stop all other keys from reaching ReactGrid
                e.stopPropagation();
            }}
        />
    );
};
